import React, { useEffect, useMemo, useState } from 'react';
import {
  VStack,
  Text,
  HStack,
  Button,
  CheckboxListItem,
  CheckboxListCard,
} from '@adc/parallax-component-library';
import { Dialog } from 'tamagui';
import { AccountWithRoleAssignments, Role } from '../../models';
import i18n from '../../utilities/i18n';
import RoleAssignmentsConfirmationModal from './RoleAssignmentsConfirmationModal';
import MessageBanner from '../messageBanner/MessageBanner';
import './Modal.scss';

interface RoleAssignmentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedAccount: AccountWithRoleAssignments;
  availableRoles: Role[];
  isSaving?: boolean;
  onSave: (addRoleKeys: string[], removeRoleKeys: string[]) => Promise<void>;
}

/**
 * Modal for viewing and editing role assignments for a selected account
 * @param {boolean} isOpen - Whether the modal is open
 * @param {() => void} onClose - Callback when modal closes
 * @param {AccountWithRoleAssignments} selectedAccount - Selected account to assign roles to
 * @param {Role[]} availableRoles - Available roles to assign
 * @param {(addRoleKeys: string[], removeRoleKeys: string[]) => Promise<void>} onSave - Callback with role key updates
 * @returns {React.JSX.Element}
 */
export default function RoleAssignmentsModal({
  isOpen,
  onClose,
  selectedAccount,
  availableRoles,
  isSaving = false,
  onSave,
}: RoleAssignmentsModalProps) {
  const originalRoleKeys = useMemo(
    () =>
      selectedAccount.roleAssignments.map(
        (roleAssignment) => roleAssignment.role.key,
      ),
    [selectedAccount.roleAssignments],
  );

  const [selectedRoleKeys, setSelectedRoleKeys] =
    useState<string[]>(originalRoleKeys);

  const { addRoleKeys, removeRoleKeys } = useMemo(
    () => ({
      addRoleKeys: selectedRoleKeys.filter(
        (key) => !originalRoleKeys.includes(key),
      ),
      removeRoleKeys: originalRoleKeys.filter(
        (key) => !selectedRoleKeys.includes(key),
      ),
    }),
    [originalRoleKeys, selectedRoleKeys],
  );

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [hasSaveError, setHasSaveError] = useState(false);

  const handleRoleToggle = (roleKey: string) => {
    if (selectedRoleKeys.includes(roleKey)) {
      setSelectedRoleKeys(selectedRoleKeys.filter((key) => key !== roleKey));
    } else {
      setSelectedRoleKeys([...selectedRoleKeys, roleKey]);
    }
  };

  const handleSave = () => {
    if (isSaving) {
      return;
    }

    setHasSaveError(false);
    setShowConfirmation(true);
  };

  const onConfirmSave = async () => {
    if (isSaving) {
      return;
    }

    try {
      await onSave(addRoleKeys, removeRoleKeys);
      setHasSaveError(false);
      setShowConfirmation(false);
      onClose();
    } catch {
      setShowConfirmation(false);
      setHasSaveError(true);
    }
  };

  // sync selected roles when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedRoleKeys(originalRoleKeys);
      setShowConfirmation(false);
      setHasSaveError(false);
    }
  }, [isOpen, originalRoleKeys]);

  const accountName = `${selectedAccount.firstName} ${selectedAccount.lastName}`;

  return (
    <>
      <Dialog open={isOpen}>
        <Dialog.Portal>
          <Dialog.Overlay key="overlay" />
          <Dialog.Content key="content" width="75%" maxHeight="90%">
            <VStack gap="$2">
              <Dialog.Title>
                <Text type="$h3.strong">
                  {i18n.t('Modal_RoleAssignments.title')}
                </Text>
              </Dialog.Title>
            </VStack>

            <VStack gap="$2" paddingVertical="$4" flex={1}>
              {hasSaveError ? (
                <MessageBanner
                  testID="account-assignments-save-error-banner"
                  type="danger"
                  iconSize="M"
                  headerText={i18n.t('Modal_RoleAssignments.error_header')}
                  bodyText={i18n.t('Modal_RoleAssignments.error_generic')}
                />
              ) : null}

              <VStack gap="$2" paddingBottom="$4">
                <Text type="$bodyBase.strong">
                  {i18n.t('Modal_RoleAssignments.selected_account')}
                </Text>
                <Text
                  type="$bodyBase.default"
                  testID="account-assignments-account-name"
                >
                  {accountName}
                </Text>
              </VStack>

              <VStack gap="$2" flex={1} minHeight={0}>
                <Text type="$bodyBase.strong">
                  {i18n.t('Modal_RoleAssignments.roles')}
                </Text>
                <VStack flex={1} minHeight={0} overflow="scroll">
                  <CheckboxListCard separated>
                    {availableRoles.map((role) => {
                      return (
                        <CheckboxListItem
                          key={role.key}
                          title={role.name}
                          label={role.description}
                          checked={selectedRoleKeys.includes(role.key)}
                          onPress={() => handleRoleToggle(role.key)}
                          testID={`role-checkbox-${role.key}`}
                          aria-label={`${role.key} - ${role.description}`}
                        />
                      );
                    })}
                  </CheckboxListCard>
                </VStack>
              </VStack>
            </VStack>

            <HStack gap="$2" justifyContent="flex-end">
              <Button
                variant="secondary"
                size="medium"
                label={i18n.t('Common.cancel')}
                onPress={onClose}
                disabled={isSaving}
                testID="account-assignments-cancel-button"
              />
              <Button
                variant="primary"
                size="medium"
                label={i18n.t('Common.save')}
                onPress={handleSave}
                disabled={
                  isSaving ||
                  (addRoleKeys.length === 0 && removeRoleKeys.length === 0)
                }
                testID="account-assignments-save-button"
              />
            </HStack>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>

      <RoleAssignmentsConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={onConfirmSave}
        selectedAccount={selectedAccount}
        availableRoles={availableRoles}
        selectedRoleKeys={selectedRoleKeys}
        addRoleKeys={addRoleKeys}
        removeRoleKeys={removeRoleKeys}
        isSaving={isSaving}
      />
    </>
  );
}
